import axios from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {context, propagation, trace} from "@opentelemetry/api";
import {setHyperDXAttributes} from "./otel";

const apiUrl = window.REACT_APP_MAPLE_API || "http://localhost:8003";
const authUrl = window.REACT_APP_MAPLE_AUTH || "http://localhost:3000";
const uiUrl = window.REACT_APP_MAPLE_UI || "http://localhost:9080";
const xerxesUrl = window.REACT_APP_XERXES_URL || "https://xerxes-stage.traxion.xfinity.tv/xerxes-sp/protected/auth?tenant=a2t&client_id=Maple&response_type=code&continue=";

const tracer = trace.getTracer('maple-ui-react');
const TOKEN_EXPIRATION_BUFFER = 330000; // 5 mins 30 seconds

export const isWafResponse = (data) => {
  if (typeof data === 'string' && data.includes('Access to the')) {
    return true;
  }
  return false;
};

export const extractWafSupportId = (data) => {
  if (typeof data !== 'string') return null;
  const match = data.match(/support\s+ID\s+is[:\s]*(\d+)/i);
  return match ? match[1] : null;
};

export const buildWafErrorMessage = (data) => {
  const supportId = extractWafSupportId(data);
  let msg = "Your query was blocked by the web application firewall (WAF).";
  if (supportId) {
    msg += `\nSupport ID: ${supportId}.`;
  }
  msg += "\nPlease contact #waf-support on Slack with the Support ID.";
  msg += "\nYou can also create a ticket at {{TECHSPOT_LINK}} (Symptom code: WAF-Application issue)";
  msg += "\nand email WAFSUPPORT@comcast.com with the ticket number and a brief description of the issue.";
  return msg;
};

axios.interceptors.response.use(
  (response) => {
    if (isWafResponse(response.data)) {
      const error = new Error(buildWafErrorMessage(response.data));
      error.isWaf = true;
      console.error("WAF BLOCKED — full response:", response.data);
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    if (error.response && isWafResponse(error.response.data)) {
      const wafError = new Error(buildWafErrorMessage(error.response.data));
      wafError.isWaf = true;
      console.error("WAF BLOCKED — full response:", error.response.data);
      return Promise.reject(wafError);
    }
    return Promise.reject(error);
  }
);

let sessionExpiredCallback = null;
let intervalId = null;

export const setSessionExpiredCallback = (callback) => {
  sessionExpiredCallback = callback;
};

export const _getSessionExpiredCallback = () => sessionExpiredCallback;

export const startTokenExpirationCheck = () => {
  const checkTokenExpiration = () => {
    const token = sessionStorage.getItem("mapleToken");
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();

      if (expirationTime - currentTime <= TOKEN_EXPIRATION_BUFFER) {
        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        } else {
          console.error("Session expired callback not set.");
        }
        clearInterval(intervalId); 
        intervalId = null;
        return;
      }
    } catch (error) {
        console.error("Invalid token: ", error);
        logOutAndRedirect();
        throw new Error("Invalid token");
    }
  };

  if (!intervalId) {
    intervalId = setInterval(checkTokenExpiration, 1000); // Check every second
  }
};

export const azureLogin = async () => {
  try {
    window.location.href =
      authUrl + "/azure/maple?returnUrl=" + uiUrl + "/auth";
    console.log("Azure login successful");
  } catch (error) {
    console.error("Error logging in", error);
    throw error;
  }
};

export const logOutAndRedirect = () => {
  sessionStorage.removeItem("mapleAccessToken");
  sessionStorage.removeItem("user");
  Cookies.remove("mapleAccessToken");
  window.location.href = uiUrl + "/login";
};

export const saveUserSession = () => {
  const token = sessionStorage.getItem("mapleToken");
  if (!token) return;
  const decoded = jwtDecode(token);
  const hasAdminRights =
    decoded.roles && decoded.roles.includes("operations");
  const userDetails = {
    name: decoded.displayName || "",
    user_id: decoded.username || "",
    email: decoded.email || "",
    admin: hasAdminRights,
    loginType: localStorage.getItem('loginType') || "",
  };
  const userRoles = decoded.roles || [];

  sessionStorage.setItem("user", JSON.stringify(userDetails));
  setHyperDXAttributes(userDetails, userRoles);
};

export const apiGetRequest = async (endpoint) => {
  return tracer.startActiveSpan('HTTP GET', async (span) => {
    try {
      const url = apiUrl + endpoint;
      const headers = getAuthHeader();

      // inject context to trace headers and add span attributes
      propagation.inject(context.active(), headers.headers);
      span.setAttribute('http.url', url);
      span.setAttribute("user.id", sessionStorage.getItem("user") ? JSON.parse(sessionStorage.getItem("user")).user_id : "unknown");

      const response = await axios.get(url, headers);

      span.setAttribute("status", response.status);
      span.setAttribute("exception", "none")
      return response.data;
    } catch (error) {
      console.error("API ERROR:", error);
      if (error.response && error.response.status === 500) {
        console.error("Server error occurred (500), redirecting to login.");
        span.setAttribute("status", error.response.status);
        span.setAttribute("exception", error.response.data || "unknown");
        window.location.href = uiUrl + "/login";
      }

      throw error;
    } finally {
      span.end();
    }
  });
};

export const chPostRequest = async (
  endpoint,
  requestBody,
  onProgress,
  cancelToken
) => {
  return tracer.startActiveSpan('HTTP POST', async (span) => {
    try {
      const chUrl = apiUrl + "/clickhouse_http/" + endpoint;
      const headers = getAuthHeader();

      // inject context to trace headers and add span attributes
      propagation.inject(context.active(), headers.headers);
      span.setAttribute('http.url', chUrl);
      span.setAttribute('request.sql', requestBody.sql);
      span.setAttribute("user.id", sessionStorage.getItem("user") ? JSON.parse(sessionStorage.getItem("user")).user_id : "unknown");

      let totalUpload = 0;
      let totalDownload = 0;

      const response = await axios.post(chUrl, requestBody, {
        ...headers,
        cancelToken: cancelToken,
        onUploadProgress: (progressEvent) => {
          totalUpload = progressEvent.total || 0;
          const uploaded = progressEvent.loaded;
          const total = totalUpload + totalDownload;
          const progress = Math.round((uploaded / total) * 50);
          if (onProgress) onProgress(progress);
        },
        onDownloadProgress: (progressEvent) => {
          totalDownload = progressEvent.total || 0;
          const downloaded = progressEvent.loaded;
          const total = totalUpload + totalDownload;
          const progress = 50 + Math.round((downloaded / total) * 50);
          if (onProgress) onProgress(progress);
        },
      });

      span.setAttribute("status", response.status);
      span.setAttribute("exception", "none")
      return response.data;
    } catch (error) {
      console.error("API ERROR:", error);
      if (error.response) {
        const errorMessage = error.response.data?.message || "An error occurred while processing your request.";
        span.setAttribute("status", error.response.status);
        span.setAttribute("exception", error.response.data || "unknown");
        throw new Error(errorMessage);
      }
      throw error;
    } finally {
      span.end();
    }
  });
};

export const queryPostRequest = async (endpoint, requestBody) => {
  try {
    const queryUrl = apiUrl + "/query/" + endpoint;
    const authHeader = getAuthHeader();
    const response = await axios.post(queryUrl, requestBody, authHeader);
    return response.data;
  } catch (error) {
    console.error("API ERROR:", error);
    throw error;
  }
};

export const loginXerxes = async () => {
  window.location.href = xerxesUrl + uiUrl + "/auth?type=xerxes-user";
};

export const getAuthHeader = () => {
  const token = sessionStorage.getItem("mapleToken");
  if (token) {
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  logOutAndRedirect(); // Redirect when access token isn't set
  throw new Error("Bearer token not set!");
};

export const apiPostRequest = async (endpoint, requestBody) => {
  try {
    const postUrl = apiUrl + endpoint;
    const response = await axios.post(postUrl, requestBody);
    return response.data;
  } catch (error) {
    console.error("API ERROR:", error);
    throw error;
  }
};